import type { CancelTokenSource } from 'axios'

import type { Nullable } from '@/types/Nullable'
import type { StoreModel } from '@/types/StoreModel'
import extractData from '@/utils/extractData'
import type { LeafletForm } from '@/views/dashboard/types/LeafletForm'

import type { LeafletUpdateQueryParams } from '../leaflets'
import {
  addLeafletCover,
  addLeafletFiles,
  type AllLeafletsPredefinedFilter,
  fetchLeafletDetails,
  getAllLeaflets,
  getLeafletById,
  type LeafletApiModel,
  type LeafletDetailsModel,
  type LeafletModel,
  putLeaflet
} from '../leaflets'
import { leafletsMapper } from '../mappers/leaflets/leafletsMapper'
import { expectLeafletMutationStatus } from './leafletMutationStatus'

export async function fetchLeaflets(
  params: AllLeafletsPredefinedFilter,
  cancelToken: Nullable<CancelTokenSource>
): Promise<LeafletModel[]> {
  const response = await getAllLeaflets(params, cancelToken)
  const data = extractData(response)
  return leafletsMapper.mapLeafletListFromApi(data)
}

export async function fetchLeaflet(leafletId: LeafletApiModel['id']): Promise<LeafletModel> {
  const response = await getLeafletById(leafletId)
  const data = extractData(response)
  return leafletsMapper.mapLeafletFromApi(data)
}

export async function updateLeaflet(
  payload: LeafletForm,
  storeId: StoreModel['id'],
  queryParams?: LeafletUpdateQueryParams
) {
  const apiPayload = leafletsMapper.mapLeafletUpdateToApi(payload)
  const response = await putLeaflet(apiPayload, storeId, queryParams)
  return expectLeafletMutationStatus(response, 204)
}

export async function uploadLeafletCover(data: FormData, leafletId: LeafletApiModel['id']) {
  return addLeafletCover(data, leafletId)
}

export async function uploadLeafletFiles(data: FormData, leafletId: LeafletApiModel['id'], watermark: boolean) {
  return addLeafletFiles(data, leafletId, watermark)
}

export async function getLeafletDetails(leafletId: LeafletApiModel['id']): Promise<LeafletDetailsModel> {
  const response = await fetchLeafletDetails(leafletId)
  const data = extractData(response)
  return leafletsMapper.mapLeafletDetailsFromApi(data)
}
